import { useCallback, useState } from 'react';

import { useRouter } from 'expo-router';

import {
  ILoyaltyUserNotificationsQuery,
  useLoyaltyUserNotificationsLazyQuery,
  useMarkLoyaltyNotificationsAsReadMutation,
} from 'generated/graphql-gateway';
import logger from 'utils/logger';
import { routes } from 'utils/routing';

import { ChallengeCompletedModalData, IUseLoyaltyUserNotifications } from './types';

/**
 * Custom hook that manages the lifecycle of loyalty notifications for a user.
 *
 * It handles:
 * - Fetching user notifications from the GraphQL API
 * - Marking notifications as read
 * - Refetching the notification list when needed
 *
 */
export const useLoyaltyUserNotifications = ({
  loyaltyId,
}: {
  loyaltyId?: string;
}): IUseLoyaltyUserNotifications => {
  const { navigate } = useRouter();
  const [challengeCompletedModalData, setChallengeCompletedModalData] = useState<
    ChallengeCompletedModalData
  >({ monthlyChallengePoints: 0, profileChallengePoints: 0 });
  const [loyaltyNotifications, setLoyaltyNotifications] = useState<
    ILoyaltyUserNotificationsQuery['loyaltyUserV2']['notifications']
  >([]);

  const [
    loyaltyUserNotificationsQuery,
    { loading: loadingNotifications },
  ] = useLoyaltyUserNotificationsLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: data => {
      setLoyaltyNotifications(data?.loyaltyUserV2?.notifications || []);
    },
  });

  const [
    markNotificationsAsRead,
    { loading: markAsReadLoading },
  ] = useMarkLoyaltyNotificationsAsReadMutation();

  const fetchLoyaltyNotifications = useCallback(async () => {
    if (loyaltyId) {
      await loyaltyUserNotificationsQuery({
        variables: { loyaltyId, where: { wasRead: false } },
      });
    }
  }, [loyaltyId, loyaltyUserNotificationsQuery]);

  const markLoyaltyNotificationsAsRead = useCallback(
    async (ids: string[]) => {
      if (!ids.length) {
        return;
      }

      try {
        await markNotificationsAsRead({ variables: { input: { ids } } });
      } catch (error) {
        logger.warn({
          error,
          message: `Failed to mark notifications as read`,
        });
      } finally {
        await fetchLoyaltyNotifications();
      }
    },
    [fetchLoyaltyNotifications, markNotificationsAsRead]
  );

  const openChallengeCompletedModal = useCallback(
    ({ monthlyChallengePoints = 0, profileChallengePoints = 0 }: ChallengeCompletedModalData) => {
      if (monthlyChallengePoints === 0 && profileChallengePoints === 0) {
        return;
      }

      setChallengeCompletedModalData({
        monthlyChallengePoints,
        profileChallengePoints,
      });

      navigate(routes.loyaltyChallengeCompleted);
    },
    [navigate]
  );

  return {
    loyaltyNotifications,
    loadingNotifications,
    markAsReadLoading,
    challengeCompletedModalData,
    markLoyaltyNotificationsAsRead,
    fetchLoyaltyNotifications,
    openChallengeCompletedModal,
  };
};
